/**
 * Shared data for the "Calendar:" row on the Details step.
 *
 * Owns the shared-calendars fetch (via `useSharedCalendarsQuery`) and
 * derives everything both the view (`CalendarField`) and the edit
 * `Select` need: summary name, "(You)" suffix, select options,
 * edit-pencil enablement, and loading state.
 */
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMailBox } from '@/hooks/use-mailbox';
import { useSharedCalendarsQuery } from '@/api/sharedCalendars';
import { useSharedCalendarSetting } from '@/api/useSharedCalendar';
import { useKFlags } from '@/hooks';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { selectUsers } from '@/store/users/selectors';
import type { SelectOption } from '@kula-ai/components';
import { CALENDAR_PROVIDERS } from '@/ui-library/components/constants';
import type { ApiReference } from '../../../config/types';
export type { SharedCalendar as SharedCal } from '@/api/sharedCalendars';
import type { SharedCalendar as SharedCal } from '@/api/sharedCalendars';

interface ScheduledCalendar {
  calendar_name?: string;
  provider_calendar_id?: string;
  is_primary?: boolean;
  user_id?: number;
  user_name?: string;
}

export interface CalendarInfoValue {
  provider_calendar_id?: string;
  is_primary?: boolean;
}

interface UseScheduleCalendarDataOptions {
  interviewPlan?: Record<string, unknown>;
  apiReference?: ApiReference;
  /** Live form value of `calendar_info` — drives summary + suffix. */
  calendarInfo?: CalendarInfoValue | null;
  /** Adhoc create has no `interviewPlan` to read
   *  `shared_calendar_enabled` from. Whenever the flag is absent (adhoc,
   *  or manual/panel edit whose `ats_interviews/:id` response omits it)
   *  the hook fetches the account setting directly
   *  (`useSharedCalendarSetting`) — same value the plan-create /
   *  self-schedule-edit responses carry inline. */
  isAdhoc?: boolean;
  /** Self-schedule edit is the only edit context where legacy allowed
   *  swapping the calendar (`SchCalendarSchedulingFields:365`). Manual
   *  edit kept the saved calendar locked. Pass `true` for any
   *  self-schedule mode so we mirror that gate. */
  isSelfSchedule?: boolean;
}

export interface ScheduleCalendarData {
  /** Whole row should be in the schema at all. */
  shouldShowRow: boolean;

  /** Calendar service is connected in the session. */
  isCalConnected: boolean;

  /** Shared-calendar poll is resolving for the first time. */
  isLoading: boolean;

  /** Raw list of non-deleted shared calendars. */
  calendars: SharedCal[];

  /** Options feed for the edit-mode native `type: 'select'`. */
  selectOptions: SelectOption[];

  /** Name shown in view mode. Empty string when nothing resolves. */
  summaryName: string;

  /** Append " (You)" in view mode — only when the resolved calendar
   *  belongs to the current user (their primary or a shared calendar
   *  they own). */
  showYouSuffix: boolean;

  /** Hide the edit pencil entirely. */
  hideEdit: boolean;

  /** Disabled-state tooltip on the edit pencil. */
  disabledTooltip?: string;

  /** Default calendar to seed the form with on create. */
  defaultCalendar?: SharedCal;

  /** Lookup helper used by the Select's onChange transform. */
  findById: (_id: string) => SharedCal | undefined;

  /** Forces a re-poll — call before opening the edit dropdown. */
  refetchUuid: () => void;

  /** True when the recruiter's connected calendar is Outlook (`mscal`).
   *  Drives the Outlook-only "organizer" hint + interviewer-chip warning. */
  isOutlook: boolean;

  /** True when the picked/hydrated calendar is the recruiter's own
   *  primary. Consumers suppress the interviewer "calendar owner"
   *  triangle on this (legacy `!calendar_info?.is_primary` chip gate);
   *  the organizer info icon itself is NOT suppressed. */
  isPrimarySelected: boolean;

  /** Owner of the currently-selected (or hydrated) shared calendar,
   *  or `null` when:
   *    - the user's calendar isn't Outlook, OR
   *    - the selected calendar is the recruiter's primary (they own
   *      the event themselves; no hint needed).
   *  Mirrors legacy `calendarOwnerUserId` + name-resolution priority
   *  in `useCalendarSchedulingFields`. */
  outlookOwner: { userId: number | null; name: string } | null;
}

export function useScheduleCalendarData({
  interviewPlan,
  apiReference,
  calendarInfo,
  isAdhoc = false,
  isSelfSchedule = false,
}: UseScheduleCalendarDataOptions): ScheduleCalendarData {
  const isEdit = apiReference === 'edit';
  // Account-level shared-calendar switch, resolved per scenario:
  //   • Plan create (`ats_interviews/new`) and self-schedule edit
  //     (`ats_schedules/show`) BOTH render `shared_calendars/settings`,
  //     so `interviewPlan.shared_calendar_enabled` is present — use it.
  //   • Adhoc has no plan, and the manual/panel edit response
  //     (`ats_interviews/:id`) OMITS the flag (it ships
  //     `meeting_rooms_enabled` but not `shared_calendar_enabled`).
  //     Whenever the flag is absent we read the account setting directly
  //     (`shared_calendars/show_setting`) — the same value those
  //     responses derive it from. Mirrors the meeting-rooms fallback.
  // Hardcoding adhoc to `true` made it call `shared_calendars/trigger_fetch`
  // even when the feature is off → 422 err_feature_not_enabled → empty row.
  const planSharedFlag = interviewPlan?.shared_calendar_enabled;
  const needsAccountSetting = typeof planSharedFlag !== 'boolean';
  const {
    data: accountSharedCalendarEnabled,
    isLoading: isSharedSettingLoading,
  } = useSharedCalendarSetting({ enabled: needsAccountSetting });
  const sharedEnabled =
    typeof planSharedFlag === 'boolean'
      ? planSharedFlag
      : Boolean(accountSharedCalendarEnabled);

  const { feSchedulingPreference } = useKFlags() as {
    feSchedulingPreference?: boolean;
  };
  const currUser = useSelector(selectCurrUser) as
    | {
        id?: number;
        name?: string;
        avatar_url?: string;
        avatar?: string;
        connected_calendar?: { gcal?: boolean; mscal?: boolean };
      }
    | null
    | undefined;
  // Account users — the only client-side source of the calendar
  // owner's display NAME. The BE omits `user_name` on both
  // `scheduled_calendar` and `shared_calendars/list`, so legacy
  // (`useCalendarSchedulingFields`) resolved it via this same Redux
  // users list keyed on the calendar's `user_id`. The new wizard had
  // dropped this lookup, regressing the hint to a static literal.
  const accountUsers = useSelector(selectUsers) as
    | Array<{ id?: number; name?: string }>
    | undefined;
  const hasConnectedCalendar = Boolean(
    currUser?.connected_calendar?.gcal || currUser?.connected_calendar?.mscal
  );
  // Base eligibility — the scheduling-preference flag is on and the
  // recruiter has a connected calendar. Whether the ROW actually shows
  // additionally depends on the shared-calendar feature (see
  // `shouldShowRow` below); this looser gate only feeds the list fetch.
  const baseRowEligible = Boolean(
    feSchedulingPreference && hasConnectedCalendar
  );

  const { isCalConnected, connectedCalValue } = useMailBox() as {
    isCalConnected: boolean;
    connectedCalValue?: string;
  };
  const isOutlook = connectedCalValue === CALENDAR_PROVIDERS.MICROSOFT;

  const {
    data: calendarData,
    isLoading: isSharedListLoading,
    isError: isSharedListError,
    error: sharedListError,
    refetch,
  } = useSharedCalendarsQuery({
    type: 'saved',
    // Only poll when the feature is on — skips the 422 entirely when off.
    enabled: baseRowEligible && isCalConnected && sharedEnabled,
  });
  // Loader spans both the adhoc account-setting resolution and the
  // shared-list poll; once the feature resolves OFF we drop straight to
  // the self calendar (no spinner).
  const isLoading =
    isSharedListLoading || (needsAccountSetting && isSharedSettingLoading);

  // The shared-list fetch is the AUTHORITATIVE feature gate. A stale plan
  // (or account) `shared_calendar_enabled === true` can disagree with the
  // account's real state — `trigger_fetch` then 422s `err_feature_not_enabled`
  // and the list comes back empty. Honour that as the feature being OFF so
  // the whole row hides instead of leaving an empty "Calendar:" row with a
  // picker that opens to nothing.
  const sharedListFeatureOff =
    isSharedListError &&
    (sharedListError as { code?: string } | null)?.code ===
      'err_feature_not_enabled';
  const sharedEffectivelyEnabled = sharedEnabled && !sharedListFeatureOff;

  // Row visibility. Hide the whole "Calendar:" item — no picker, no
  // trigger_fetch — whenever the shared-calendar feature is OFF on a
  // CREATE flow. On EDIT we still surface the saved calendar read-only
  // (legacy `useCalendarSchedulingFields`: `isEdit || shared_calendar_enabled`)
  // so an interview booked while the feature was on still shows where the
  // event lands. `sharedListFeatureOff` also hides it in the stale-flag
  // case where the plan/account claimed ON but the list 422'd.
  const shouldShowRow =
    baseRowEligible && (isEdit || sharedEnabled) && !sharedListFeatureOff;

  // True only once we KNOW the feature is off — gates the edit-mode
  // self-calendar read-only fallback so accounts that DO have shared
  // calendars don't flash the recruiter's own name before the account
  // setting / list resolves. (Create-off hides the row outright, above.)
  const sharedDisabledResolved =
    (!sharedEnabled && (!needsAccountSetting || !isSharedSettingLoading)) ||
    sharedListFeatureOff;

  const refetchUuid = useCallback(() => {
    refetch();
  }, [refetch]);

  const calendars: SharedCal[] = useMemo(
    () =>
      (calendarData ?? []).filter((c) => c?.status !== 'deleted_from_provider'),
    [calendarData]
  );

  const findById = useCallback(
    (id: string) => calendars.find((c) => c.provider_calendar_id === id),
    [calendars]
  );

  // Default-calendar seed differs by entry point:
  //   - Adhoc: workspace's `is_default` shared calendar wins (the one
  //     the admin pinned in settings); fall back to the user's
  //     `is_primary` personal calendar only when none is configured.
  //   - Plan-driven: the plan hydrates `calendar_info` upstream
  //     (`fromPlan.ts`). The fallback when the plan has none or the
  //     scheduler lacks access is the recruiter's PERSONAL calendar,
  //     not the workspace shared default — product preference, so
  //     plan authors who didn't pick a calendar stay on their own.
  const defaultCalendar = useMemo(() => {
    if (isAdhoc) {
      return (
        calendars.find((c) => c.is_default) ??
        calendars.find((c) => c.is_primary)
      );
    }
    return calendars.find((c) => c.is_primary);
  }, [calendars, isAdhoc]);

  const scheduledCal = interviewPlan?.scheduled_calendar as
    | ScheduledCalendar
    | undefined;

  // Summary for the view strip. Priority:
  //   1. Currently-selected calendar (from fetched list)
  //   2. Edit mode's `scheduled_calendar.calendar_name` (no extra fetch)
  //   3. Create mode's default calendar name
  //   4. Empty — the row shows just "Calendar:" while loading so we
  //      never pretend a fake "Organizer calendar" exists.
  const summaryName = useMemo(() => {
    const id = calendarInfo?.provider_calendar_id;
    if (id) {
      const selected = findById(id);
      if (selected?.name) return selected.name;
    }
    if (isEdit && scheduledCal?.calendar_name) {
      return scheduledCal.calendar_name;
    }
    if (!isEdit && defaultCalendar?.name) return defaultCalendar.name;
    // Feature off: no shared list to resolve a name from, but the event
    // still lands on the recruiter's own calendar — show it as the self
    // calendar. BE names a primary calendar with the user's own name
    // (`shared_calendars/list`: is_primary ? User.current.name : name).
    if (sharedDisabledResolved && currUser?.name) return currUser.name;
    return '';
  }, [
    calendarInfo?.provider_calendar_id,
    currUser?.name,
    defaultCalendar,
    findById,
    isEdit,
    scheduledCal?.calendar_name,
    sharedDisabledResolved,
  ]);

  // "(You)" only when the resolved calendar belongs to the current
  // user — their primary, or a shared calendar whose owner matches
  // `currUser.id`. The earlier `is_primary`-only check could read a
  // teammate's calendar as "(You)"; the ownerUserId match (same logic
  // the dropped avatar used) ties it to the actual user.
  const showYouSuffix = useMemo(() => {
    // Self-calendar fallback (feature off, create) is the recruiter's own.
    if (sharedDisabledResolved && !isEdit) return true;
    const userId = currUser?.id;
    const id = calendarInfo?.provider_calendar_id;
    const liveCal = id ? findById(id) : undefined;
    const isPrimaryCal =
      liveCal?.is_primary ??
      (isEdit ? scheduledCal?.is_primary : undefined) ??
      defaultCalendar?.is_primary ??
      false;
    if (isPrimaryCal) return true;
    const ownerUserId =
      liveCal?.user_id ??
      (isEdit ? scheduledCal?.user_id : undefined) ??
      defaultCalendar?.user_id;
    return (
      ownerUserId !== undefined &&
      userId !== undefined &&
      Number(ownerUserId) === Number(userId)
    );
  }, [
    calendarInfo?.provider_calendar_id,
    currUser?.id,
    defaultCalendar,
    findById,
    isEdit,
    scheduledCal?.is_primary,
    scheduledCal?.user_id,
    sharedDisabledResolved,
  ]);

  // Hide the picker whenever shared calendars are off (nothing to pick —
  // the row is the read-only self calendar), and on manual edit (legacy
  // locks the saved calendar). Self-schedule edit with the feature on
  // keeps the swap affordance. Source parity: `useCalendarSchedulingFields`
  // line 365 — `(isSelfSchedule || !isEdit) && (isEdit ? shared_calendar_enabled : true)`.
  const hideEdit = !sharedEffectivelyEnabled || (isEdit && !isSelfSchedule);
  const onlyPrimaryAvailable =
    calendars.length === 0 ||
    (calendars.length === 1 && calendars[0]?.is_primary);
  const disabledTooltip =
    sharedEffectivelyEnabled && !isLoading && onlyPrimaryAvailable
      ? "You don't have access to any shared calendars"
      : undefined;

  // Is the picked (or hydrated) calendar the recruiter's own primary?
  // Legacy gates the interviewer "calendar owner" triangle on
  // `!calendar_info?.is_primary` at the chip level — NOT by nulling the
  // owner. The Outlook organizer info icon stays visible for ANY
  // Outlook calendar (legacy `getCalendarLabelInfoText` is gated only
  // on `connectedCalValue === mscal`). Live form value wins so a swap
  // in the picker re-derives immediately.
  const isPrimarySelected = useMemo<boolean>(() => {
    // Self-calendar fallback (feature off, create) — the event lands on
    // the recruiter's own primary, so suppress the interviewer triangle.
    if (sharedDisabledResolved && !isEdit) return true;
    const id = calendarInfo?.provider_calendar_id;
    const liveCalendar = id ? findById(id) : undefined;
    if (liveCalendar) return Boolean(liveCalendar.is_primary);
    if (calendarInfo && 'is_primary' in calendarInfo) {
      return Boolean(calendarInfo.is_primary);
    }
    if (isEdit) return Boolean(scheduledCal?.is_primary);
    return Boolean(defaultCalendar?.is_primary);
  }, [
    calendarInfo,
    defaultCalendar,
    findById,
    isEdit,
    scheduledCal,
    sharedDisabledResolved,
  ]);

  // Outlook "this calendar is owned by X" derivation — mirrors legacy
  // `useCalendarSchedulingFields.getCalendarOwnerUserId` +
  // `getCalendarLabelInfoText`. Resolved for ANY Outlook calendar
  // (incl. primary — legacy parity); primary-suppression of the
  // interviewer triangle is the consumer's job via `isPrimarySelected`.
  const outlookOwner = useMemo<{
    userId: number | null;
    name: string;
  } | null>(() => {
    if (!isOutlook) return null;
    // Self-calendar fallback (feature off, create) — the recruiter owns
    // the calendar the event lands on, so the organizer IS them.
    if (sharedDisabledResolved && !isEdit) {
      return {
        userId: currUser?.id ?? null,
        name: currUser?.name ?? 'the calendar owner',
      };
    }
    const id = calendarInfo?.provider_calendar_id;
    const liveCalendar = id ? findById(id) : undefined;

    // Owner user id — legacy priority:
    //   1. live calendar (selected via the picker)
    //   2. hydrated `scheduled_calendar` (edit mode)
    //   3. default calendar (create mode pre-pick)
    //   4. fallback: `scheduled_by.id` (edit mode, calendar dropped out
    //      of the user's accessible list).
    const userId =
      liveCalendar?.user_id ??
      (isEdit ? scheduledCal?.user_id : undefined) ??
      defaultCalendar?.user_id ??
      (isEdit
        ? (interviewPlan?.scheduled_by as { id?: number } | undefined)?.id
        : undefined) ??
      null;

    // Owner display NAME — the BE emits no `user_name` on either
    // `scheduled_calendar` or `shared_calendars/list`, so the working
    // path (legacy parity) is: resolved `userId` → Redux users list →
    // `name`; then `scheduled_by.name`; then a literal. The `user_name`
    // probes are kept first only to honour the wire shape if BE ever
    // starts sending it.
    const usersName =
      userId !== null
        ? accountUsers?.find((u) => Number(u?.id) === Number(userId))?.name
        : undefined;
    const name =
      liveCalendar?.user_name ||
      (isEdit ? scheduledCal?.user_name : undefined) ||
      defaultCalendar?.user_name ||
      usersName ||
      (isEdit
        ? (interviewPlan?.scheduled_by as { name?: string } | undefined)?.name
        : undefined) ||
      'the calendar owner';

    return { userId, name };
  }, [
    accountUsers,
    calendarInfo?.provider_calendar_id,
    currUser?.id,
    currUser?.name,
    defaultCalendar,
    findById,
    interviewPlan,
    isEdit,
    isOutlook,
    scheduledCal,
    sharedDisabledResolved,
  ]);

  const selectOptions: SelectOption[] = useMemo(
    () =>
      calendars
        .filter(
          (c): c is SharedCal & { provider_calendar_id: string } =>
            typeof c.provider_calendar_id === 'string'
        )
        .map((c) => ({
          value: c.provider_calendar_id,
          label: `${c.name ?? c.provider_calendar_id}${c.is_primary ? ' (You)' : ''}`,
        })),
    [calendars]
  );

  return {
    shouldShowRow,
    isCalConnected,
    isLoading,
    calendars,
    selectOptions,
    summaryName,
    showYouSuffix,
    hideEdit,
    disabledTooltip,
    defaultCalendar,
    findById,
    refetchUuid,
    isOutlook,
    isPrimarySelected,
    outlookOwner,
  };
}
